import * as React from "react"
import { createPortal } from "react-dom"
import type { Data } from "rete/types/core/data"
import { chartDetailIcon, chartEntryHasData, getChartDetailEntries, type ChartDetailEntry } from "../editor/model_detail"
import { ChartDetailBody } from "../editor/chart_detail_body"

const loadedCharts = (entries: ChartDetailEntry[]): ChartDetailEntry[] =>
  entries.filter(chartEntryHasData)

const CHART_COLOURS = 5

export function AegisChartSection({ model }: { model: Data | null }) {
  const catalog = React.useMemo(() => getChartDetailEntries(model), [model])
  const entries = React.useMemo(() => loadedCharts(catalog), [catalog])
  const [openId, setOpenId] = React.useState<number | null>(null)
  const openEntry = entries.find(entry => entry.nodeId === openId) ?? null
  const trackRef = React.useRef<HTMLDivElement>(null)
  const knownIdsRef = React.useRef<Set<number> | null>(null)
  const scrolledFreshRef = React.useRef<Set<number>>(new Set())
  const [freshIds, setFreshIds] = React.useState<Set<number>>(() => new Set())
  const [canPrev, setCanPrev] = React.useState(false)
  const [canNext, setCanNext] = React.useState(false)

  React.useEffect(() => {
    const ids = catalog.map(entry => entry.nodeId)
    if (knownIdsRef.current == null) {
      knownIdsRef.current = new Set(ids)
      return
    }
    const arrived = ids.filter(id => !knownIdsRef.current!.has(id))
    for (const id of ids) knownIdsRef.current.add(id)
    if (arrived.length === 0) return
    setFreshIds(prev => {
      const next = new Set(prev)
      for (const id of arrived) next.add(id)
      return next
    })
  }, [catalog])

  React.useEffect(() => {
    const newest = Array.from(freshIds).reverse().find(id =>
      entries.some(entry => entry.nodeId === id) && !scrolledFreshRef.current.has(id)
    )
    if (newest == null) return
    scrolledFreshRef.current.add(newest)
    window.requestAnimationFrame(() => {
      trackRef.current
        ?.querySelector(`[data-chart-id="${newest}"]`)
        ?.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" })
    })
  }, [entries, freshIds])

  React.useLayoutEffect(() => {
    const track = trackRef.current
    if (!track) return
    const update = () => {
      setCanPrev(track.scrollLeft > 4)
      setCanNext(track.scrollLeft + track.clientWidth < track.scrollWidth - 4)
    }
    update()
    track.addEventListener("scroll", update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(track)
    return () => {
      track.removeEventListener("scroll", update)
      observer.disconnect()
    }
  }, [entries.length])

  React.useEffect(() => {
    if (openEntry == null) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenId(null)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [openEntry])

  if (entries.length === 0) {
    return null
  }

  const scrollByPage = (direction: number) => {
    const track = trackRef.current
    if (!track) return
    track.scrollBy({ left: direction * Math.max(track.clientWidth * 0.8, 140), behavior: "smooth" })
  }

  const openChart = (nodeId: number) => {
    setOpenId(nodeId)
    setFreshIds(prev => {
      if (!prev.has(nodeId)) return prev
      const next = new Set(prev)
      next.delete(nodeId)
      return next
    })
  }

  const showNav = canPrev || canNext

  return (
    <div className="aegis-chart-section">
      {showNav && (
        <button
          type="button"
          className="aegis-chart-section__nav"
          onClick={() => scrollByPage(-1)}
          disabled={!canPrev}
          aria-label="Previous graphs"
        >
          <i className="fas fa-chevron-left" aria-hidden />
        </button>
      )}
      <div className="aegis-chart-section__track" ref={trackRef}>
        {entries.map((entry, index) => {
          const isNew = freshIds.has(entry.nodeId)
          return (
            <button
              key={entry.nodeId}
              type="button"
              data-chart-id={entry.nodeId}
              className={`aegis-chart-section__button aegis-chart-section__button--c${index % CHART_COLOURS}${isNew ? " aegis-chart-section__button--new" : ""}`}
              onClick={() => openChart(entry.nodeId)}
            >
              <i
                className={`fas ${chartDetailIcon(entry.kind)}`}
                aria-hidden
              />
              <span className="aegis-chart-section__name">{entry.name}</span>
              {isNew && <span className="aegis-chart-section__new">New</span>}
            </button>
          )
        })}
      </div>
      {showNav && (
        <button
          type="button"
          className="aegis-chart-section__nav"
          onClick={() => scrollByPage(1)}
          disabled={!canNext}
          aria-label="Next graphs"
        >
          <i className="fas fa-chevron-right" aria-hidden />
        </button>
      )}
      {openEntry != null && (
        <AegisChartFullscreen entry={openEntry} onClose={() => setOpenId(null)} />
      )}
    </div>
  )
}

function AegisChartFullscreen({
  entry,
  onClose,
}: {
  entry: ChartDetailEntry
  onClose: () => void
}) {
  const titleId = `aegis-chart-title-${entry.nodeId}`
  return createPortal(
    <div className="aegis-chart-fullscreen" role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={onClose}>
      <div className="aegis-chart-fullscreen__panel" onClick={event => event.stopPropagation()}>
        <header className="aegis-chart-fullscreen__header">
          <h2 id={titleId} className="aegis-chart-fullscreen__title">{entry.name}</h2>
          <button type="button" className="aegis-chart-fullscreen__close" onClick={onClose} aria-label="Close chart">
            <i className="fas fa-times" aria-hidden />
          </button>
        </header>
        <div className={`aegis-chart-fullscreen__body${entry.kind === "sankey" ? " aegis-chart-fullscreen__body--sankey" : ""}`}>
          <ChartDetailBody entry={entry} expanded contained />
        </div>
      </div>
    </div>,
    document.body
  )
}
